import React, { Component, Fragment, Suspense, useEffect } from "react";
import {
  HashRouter as Router,
  Redirect,
  Route,
  Switch,
  useLocation,
  matchPath,
} from "react-router-dom";

//Component Imports
import Authentication from "./Core/Authentication";
import Routes from "./Core/Routes";
import Loader from "./Core/Loader/Loader";
// Eager, not lazy: it is what the SPR pages show while their own lazy chunk loads.
import SprPageSkeleton from "./Components/SprV2/components/SprPageSkeleton/SprPageSkeleton";
import SprHostApp from "./Components/SprV2/SprHostApp/SprHostApp";
import { isSprHost } from "./Components/SprV2/services/utils/sprHost";
import SideNavigationBar from "./Components/SideNavbar/SideNavigationBar";
import { decrypt } from "./utils/others";
import { getRole, isMentorLikeRole } from "./utils/auth";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import FirstClassFeedback from "./Components/ParentFeedback/FirstClassFeedback";
import SideNavbar from "./Components/SideNavbar/SideNavbar";
import { useHasPermission } from "./utils/usePermission";
import StudentAlreadyPausedModal from "./Components/StudentPause/StudentAlreadyPausedModal";
import { MentorProfile } from "./Components/MentorProfile/MentorProfile";
import { components } from "react-select";
import { permissions } from "./utils/constants/Permissions";
import appStyles from "./app.module.css";
import { useFetchDashboardProfile } from "./Components/MentorDashboardV2/services/apis";
import OtpResetPopup from "./Components/MentorDashboard/PasswordPopups";
import { ChatUnreadProvider } from "./Components/Chat/ChatUnreadContext";
import { UnwatchedCountProvider } from "./Components/MyCourses/UnwatchedCountContext";
import {
  FeatureFlagProvider,
  FLAGS,
  useFeatureFlags,
  useFeatureFlagsReady,
} from "./featureFlags";
import {
  SPR_NO_LAYOUT_ROUTES,
  SPR_PARENT_ROUTE,
  SPR_PREVIEW_ROUTE,
  SPR_REVIEW_ROUTE,
  SPR_SENT_PREVIEW_ROUTE,
  SPR_V1_REPORT_ROUTE,
} from "./Components/SprV2/services/utils/constants";

/* Routes that render full-page, outside the sidebar/header layout. */
const NO_LAYOUT_PATHS = ["/view-file/:url", ...SPR_NO_LAYOUT_ROUTES];

const SuccessPayment = React.lazy(
  () => import("./Components/BootCamp/SuccessPayment/SucessPayment"),
);
//Lazy Imports
const Login = React.lazy(() => import("./Components/Login/Login"));
const NativeComponent = React.lazy(
  () => import("./Components/NativeComponents"),
);
const Demos = React.lazy(() => import("./Components/Demos/Demos"));
const MentorDashboardNew = React.lazy(
  () => import("./Components/MentorDashboard/MentorDashboardNew"),
);
const MentorDashboardV2 = React.lazy(
  () => import("./Components/MentorDashboardV2/index"),
);
const MentorResources = React.lazy(
  () => import("./Components/MentorResources/MentorResources"),
);
const BatchRequestManagement = React.lazy(
  () => import("./Components/AdminLoop/index"),
);
const AddBatchLoop = React.lazy(
  () => import("./Components/AdminLoop/AddBatchRequestLoop"),
);
const EditBatchLoop = React.lazy(
  () => import("./Components/AdminLoop/EditBatchRequestLoop"),
);

const Classes = React.lazy(() => import("./Components/Classes/Classes"));
const BootCamp = React.lazy(
  () => import("./Components/BootCamp/BootCampRouter"),
);
const Mentors = React.lazy(
  () => import("./Components/MentorProfile/MentorProfile"),
);
const ViewDetails = React.lazy(
  () => import("./Components/Mentors/ViewDetails"),
);
const ViewConversion = React.lazy(
  () => import("./Components/Mentors/ViewConversion"),
);
const Glimpse = React.lazy(() => import("./Components/Glimpse"));
const FeedbackForm = React.lazy(
  () => import("./Components/Glimpse/FeedbackForm"),
);
const TimeTable = React.lazy(() => import("./Components/TimeTable/TimeTable"));
const MentorProfilePage = React.lazy(
  () => import("./Components/MentorProfile/PublicMentorProfile"),
);
const UserManagement = React.lazy(
  () => import("./Components/UserManagement/UserManagement"),
);
const MentorExitPage = React.lazy(() => import("./Components/MentorExit"));
const StudentPortfolio = React.lazy(
  () => import("./Components/StudentPortfolio/StudentPortfolio"),
);
const BootCampLandingPage = React.lazy(
  () => import("./Components/BootCamp/BootCampLandingPage/BootCampLandingPage"),
);
const Courses = React.lazy(() => import("./Components/Courses/Courses"));
const Promotions = React.lazy(
  () => import("./Components/Promotions/Promotions"),
);
const AddOrUpdatePromotions = React.lazy(
  () =>
    import("./Components/Promotions/AddOrUpdatePromotions/AddOrUpdatePromotions"),
);

const StudentProfileReport = React.lazy(
  () => import("./Components/StudentProfileReport/StudentProfileReport"),
);

// SPR v2 (SPRIN-3315) — review/preview are flagged routes, the parent page is public.
const SprReviewPage = React.lazy(
  () => import("./Components/SprV2/pages/SprReviewPage/SprReviewPage"),
);
const SprPreviewPage = React.lazy(
  () => import("./Components/SprV2/pages/SprPreviewPage/SprPreviewPage"),
);
const SprParentPage = React.lazy(
  () => import("./Components/SprV2/pages/SprParentPage/SprParentPage"),
);
const SprSentPreviewPage = React.lazy(
  () => import("./Components/SprV2/pages/SprSentPreviewPage/SprSentPreviewPage"),
);

const AffiliateDemos = React.lazy(
  () => import("./Components/AffiliateDemos/AffiliateDemos"),
);

const StudentManageMent = React.lazy(
  () => import("./Components/StudentManagement/Index"),
);

const StudentTestHistory = React.lazy(
  () => import("./Components/StudentTestHistory"),
);

const SelectBatch = React.lazy(
  () => import("./Components/SelectBatchPages/SelectBatchPage"),
);

const Alchemy = React.lazy(() => import("./Components/Alchemy/Alchemy"));
const AlchemyFilePreview = React.lazy(
  () => import("./Components/Alchemy/AlchemyFilePreview/AlchemyFilePreview"),
);

const ParentFeedback = React.lazy(
  () => import("./Components/ParentFeedback/ParentFeedback"),
);

const SuccessFeedback = React.lazy(
  () => import("./Components/ParentFeedback/SuccessFeedback"),
);

const ApproveExtraClassReq = React.lazy(
  () => import("./Components/Classes/ViewClass/ApproveExtraClassReq"),
);

const Slots = React.lazy(() => import("./Components/Slots/Slots"));

const Community = React.lazy(() => import("./Components/Community/Community"));

const StoreFront = React.lazy(() => import("./Components/Storefront"));

const ShiftManagement = React.lazy(
  () => import("./Components/ShiftManagement/ShiftManagement"),
);

// D-117: hidden staging test page for the mentor shift worker (the API answers 404 outside staging).
const MentorShiftTest = React.lazy(
  () => import("./Components/MentorShiftTest/MentorShiftTest"),
);

const MobileNavBar = React.lazy(
  () => import("./Components/MobileScreens/Navbar"),
);

const MobileDahsboard = React.lazy(
  () => import("./Components/MobileScreens/mobileDashboard"),
);

const AddSchedule = React.lazy(
  () => import("./Components/MobileScreens/AddSchedule"),
);

const AutoAddSlotMobile = React.lazy(
  () => import("./Components/MobileScreens/AutoAddSlotMobile"),
);

const DisableSlot = React.lazy(
  () => import("./Components/MobileScreens/DisableSlot"),
);
const viewHistory = React.lazy(
  () => import("./Components/Glimpse/DetailedHistory"),
);
const ViewStudentList = React.lazy(
  () => import("./Components/Glimpse/ViewStudentList"),
);

const Topics = React.lazy(() => import("./Components/Glimpse/Topics"));

const ChatWidget = React.lazy(
  () => import("./Components/MobileScreens/ChatWidget"),
);
const NoPermission = React.lazy(
  () => import("./Components/NoPermission/NoPermission"),
);
const ViewFile = React.lazy(() => import("./Components/Mentors/ViewFile"));
const TeacherDayPosts = React.lazy(
  () => import("./Components/TeachersDayPost/index"),
);
const Loop = React.lazy(() => import("./Components/Loop"));
const MentorTask = React.lazy(() => import("./Components/Task/MentorTask"));
const Events = React.lazy(() => import("./Components/Events"));
const Referrals = React.lazy(() => import("./Components/Referrals"));
const Referral = React.lazy(
  () => import("./Components/MentorReferrals/MentorReferral"),
);
const ReferMentors = React.lazy(
  () => import("./Components/MentorReferrals/ReferMentors"),
);
const BulkCancellation = React.lazy(
  () => import("./Components/BulkCancellation"),
);
const Payout = React.lazy(() => import("./Components/Payout/payout"));
const CyChat = React.lazy(() => import("./Components/Chat/CyChatPlugin"));
const LoopAction = React.lazy(
  () => import("./Components/LoopAction/LoopAction"),
);
const MentorLMS = React.lazy(() => import("./Components/MentorLMS/MentorLMS"));
const FeatureFlagsDebug = React.lazy(
  () => import("./Components/FeatureFlagsDebug/FeatureFlagsDebug"),
);
const ImpersonateApp = React.lazy(
  () => import("./Components/ImpersonateApp/ImpersonateApp"),
);
const MyCourses = React.lazy(() => import("./Components/MyCourses/MyCourses"));
const LearningSummary = React.lazy(
  () => import("./Components/LearningSummary/LearningSummary"),
);

const ProtectedRoute = () => {
  const roles = getRole();
  const hasPermission = useHasPermission();
  const isFlagOn = useFeatureFlags();
  const flagsReady = useFeatureFlagsReady();
  const theme = useTheme();
  const location = useLocation();
  const mobileScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const routeConfigs = [
    {
      path: "/chat",
      component: CyChat,
      exact: false,
      permission: false,
    },
    {
      path: "/demos",
      component: Demos,
      exact: false,
      permission: "demosPage",
    },
    {
      path: "/mentorDashboard",
      component: MentorDashboardV2,
      exact: false,
      permission: "mentorDashboardV2",
    },
    {
      path: "/loop",
      component:
        roles == "mentor"
          ? Loop
          : (roles === "admin" ||
              roles === "cs_person" ||
              roles === "Moderator") &&
            BatchRequestManagement,
      exact: false,
      permission: "loop",
    },
    {
      path: "/mobileDashboard",
      component: MobileDahsboard,
      exact: false,
      permission: false,
    },
    {
      path: "/mentorResources",
      component: MentorResources,
      exact: false,
      permission: false,
    },
    {
      path: "/affiliateDemos",
      component: AffiliateDemos,
      exact: false,
      permission: false,
    },
    {
      path: "/classes",
      component: Classes,
      exact: false,
      permission: "classesPage",
    },
    {
      path: "/timetable",
      component: TimeTable,
      exact: true,
      permission: false,
    },
    {
      path: "/mentors",
      component: Mentors,
      exact: true,
      permission: false,
    },
    {
      path: "/student-manage",
      component: StudentManageMent,
      exact: true,
      permission: false,
    },
    {
      path: "/student-manage/test-history",
      component: StudentTestHistory,
      exact: true,
      permission: false,
    },
    {
      path: "/mentor/view-class-details/:month/:year",
      component: ViewDetails,
      exact: true,
      permission: false,
    },
    {
      path: "/mentor/view-demo-details/:month/:year",
      component: ViewDetails,
      exact: true,
      permission: false,
    },
    {
      path: "/view-conversion/:month/:year",
      component: ViewConversion,
      exact: true,
      permission: "viewConversions",
    },
    {
      path: "/glimpse",
      component: Glimpse,
      exact: true,
      permission: "GlmipsePage",
    },
    {
      path: "/mentor-feedback/:classId",
      component: FeedbackForm,
      exact: true,
      permission: false, // Mentor specific
    },
    {
      path: "/topics",
      component: Topics,
      exact: true,
      permission: "manageTopics",
    },
    {
      path: "/view-history/:studentId/:courseId",
      component: viewHistory,
      exact: true,
      permission: "viewStudentHistory",
    },
    {
      path: "/view-student-list",
      component: ViewStudentList,
      exact: true,
      permission: "viewStudentList",
    },
    {
      path: "/add-schedule",
      component: AddSchedule,
      exact: true,
      permission: "addSchedule",
    },
    {
      path: "/add-batch-loop",
      component: AddBatchLoop,
      exact: true,
      permission: "addBatchLoop",
    },
    {
      path: "/change-batch-loop",
      component: EditBatchLoop,
      exact: true,
      permission: "EditBatchLoop",
    },
    {
      path: "/add-slot",
      component: AutoAddSlotMobile,
      exact: true,
      permission: "autoAddSlot",
    },
    {
      path: "/disable-slot",
      component: DisableSlot,
      exact: true,
      permission: "disableSlots",
    },
    {
      path: "/users",
      component: UserManagement,
      exact: true,
      permission: "userPage",
    },
    {
      path: "/mentor-exit/:mentorId",
      component: MentorExitPage,
      exact: true,
      permission: "userPage",
    },
    {
      path: "/courses",
      component: Courses,
      exact: true,
      permission: "coursesPage",
    },
    {
      path: "/promotions",
      component: Promotions,
      exact: true,
      permission: "promotions",
    },
    {
      path: "/promotions/add-or-update-promotion",
      component: AddOrUpdatePromotions,
      exact: true,
      permission: "managePromotions",
    },
    {
      path: "/slots",
      component: Slots,
      exact: false,
      permission: "viewSlots",
    },
    {
      path: "/bootcamp",
      component: BootCamp,
      exact: false,
      permission: "bootCamp",
    },
    {
      path: "/alchemy",
      component: Alchemy,
      exact: false,
      permission: "alchemy",
    },
    {
      path: "/community",
      component: Community,
      exact: false,
      permission: "Community",
    },
    {
      path: "/storefront",
      component: StoreFront,
      exact: false,
      permission: "manageStorefront",
    },
    {
      path: "/mentor-shifts",
      component: ShiftManagement,
      exact: true,
      permission: "mentorShifts",
      flag: FLAGS?.EPICENTER_MENTOR_SHIFT_MANAGEMENT?.name,
    },
    {
      path: "/mentor-shift-test",
      component: MentorShiftTest,
      exact: true,
      permission: "mentorShifts",
    },
    {
      path: "/view-file/:url",
      component: ViewFile,
      exact: true,
      permission: "mentorDashboard",
    },
    {
      path: SPR_REVIEW_ROUTE,
      component: SprReviewPage,
      loading: SprPageSkeleton,
      exact: true,
      permission: "sprReview",
      flag: FLAGS.EPICENTER_SPR_V2.name,
    },
    {
      path: SPR_PREVIEW_ROUTE,
      component: SprPreviewPage,
      loading: SprPageSkeleton,
      exact: true,
      permission: "sprReview",
      flag: FLAGS.EPICENTER_SPR_V2.name,
    },
    {
      path: SPR_SENT_PREVIEW_ROUTE,
      component: SprSentPreviewPage,
      loading: SprPageSkeleton,
      exact: true,
      permission: "sprReview",
      flag: FLAGS.EPICENTER_SPR_V2_SEND.name,
    },
    {
      path: "/events",
      component: Events,
      exact: true,
      permission: "events",
    },
    {
      path: "/referral",
      component: roles === "mentor" ? Referral : Referrals,
      exact: true,
    },
    {
      path: "/refer-mentors",
      component: ReferMentors,
      exact: true,
    },
    {
      path: "/bulk-cancellation",
      component: BulkCancellation,
      exact: true,
      permission: "bulkCancellations",
    },
    {
      path: "/payout",
      component: Payout,
      exact: true,
    },
    {
      path: "/tasks",
      component: MentorTask,
      exact: true,
      permission: "tasks",
    },
    {
      path: "/mentor-lms",
      component: MentorLMS,
      exact: false,
      permission: "mentorLMS",
    },
    {
      path: "/my-courses",
      component: MyCourses,
      exact: false,
      permission: "myCourses",
    },
    {
      path: "/learning-summary/:batchId",
      component: LearningSummary,
      exact: true,
      permission: "mentorDashboardV2",
      flag: FLAGS?.EPICENTER_LEARNING_SUMMARY?.name,
    },
  ];

  const [profileData, setProfileData] = React.useState(null);

  const { mutate: fetchProfile } = useFetchDashboardProfile({
    manual: true,
  });

  useEffect(() => {
    if (
      roles == "mentor" &&
      location.pathname !== "/mentorDashboard" &&
      localStorage.getItem("isPasswordResetDone") !== "1"
    ) {
      fetchProfile({
        onSuccess: (data) => setProfileData(data),
      });
    }
  }, [location.pathname]);

  const processedRoutes = routeConfigs.map((route) => {
    let isAllowed = false;

    if (route.permission) {
      isAllowed = hasPermission(route.permission);
    } else {
      isAllowed = true;
    }
    // `loading`: what the route shows while flags or its chunk load (default: the dots Loader).
    if (isAllowed && route.flag && !flagsReady) {
      return { ...route, component: route.loading ?? Loader, allow: true };
    }
    if (isAllowed && route.flag) {
      isAllowed = isFlagOn(route.flag);
    }
    return {
      ...route,
      component: isAllowed ? route.component : NoPermission,
      allow: isAllowed,
    };
  });

  // ✅ EARLY EXIT for full-page routes rendered without the layout
  const viewFileMatch = NO_LAYOUT_PATHS.find((path) =>
    matchPath(location.pathname, { path, exact: true }),
  );

  if (viewFileMatch) {
    const viewFileRoute = processedRoutes.find((r) =>
      matchPath(location.pathname, { path: r.path, exact: r.exact }),
    );

    const Component = viewFileRoute?.component || NoPermission;
    const match = matchPath(location.pathname, {
      path: viewFileRoute?.path,
      exact: viewFileRoute?.exact,
    });
    const Loading = viewFileRoute?.loading ?? Loader;
    return (
      <Suspense fallback={<Loading />}>
        <Component match={match} location={location} />
      </Suspense>
    );
  }

  const isMatchFullPage = [
    "/mentorDashboard",
    "/payout",
    "/referral",
    "/refer-mentors",
    "/mentors",
    "/student-manage",
    "/mentor/view-class-details/:month/:year",
    "/mentor/view-demo-details/:month/:year",
    "/chat",
  ].find((path) => matchPath(location.pathname, { path, exact: false }));

  // Mentor LMS req 5/7: a temp mentor rides this SAME branch a regular mentor
  // does (SideNavbar has no /my-courses entry at all) — SideNavigationBar
  // then further restricts which nav items they actually see.
  const isMentorLike = isMentorLikeRole(roles);

  return (
    <ChatUnreadProvider>
      <UnwatchedCountProvider>
        {isMentorLike ? (
          <div>
            <div className={appStyles.rootContainer}>
              <OtpResetPopup
                profileRes={profileData?.mentorProfileDetails ?? null}
              />
              <Suspense fallback={<Loader />}>
                {mobileScreen ? (
                  <div>
                    <MobileNavBar />
                  </div>
                ) : (
                  <div
                    className={appStyles.rootSidebar}
                    style={{
                      background:
                        location.pathname === "/loop" ? "#FFFAFA" : "#ffffff",
                    }}
                  >
                    <SideNavigationBar />
                  </div>
                )}
                <div className={appStyles.rootContent}>
                  <div
                    style={{
                      height: "100%",
                      maxHeight: "100vh",
                      maxWidth: isMatchFullPage ? "100%" : "1440px",
                      margin: isMatchFullPage ? "0" : "0 auto",
                      padding: isMatchFullPage ? "0" : "12px 16px",
                    }}
                  >
                    <Routes
                      routes={processedRoutes}
                      redirectTo={
                        roles === "affiliate_partner"
                          ? "/affiliateDemos"
                          : roles === "mentor"
                            ? "/mentorDashboard"
                            : roles === "temp_mentor"
                              ? "/my-courses"
                              : "/demos"
                      }
                    />
                  </div>
                </div>
              </Suspense>
            </div>
          </div>
        ) : (
          <div className={appStyles.adminShell}>
            <SideNavbar />
            <div className={appStyles.adminContent}>
              <Suspense fallback={<Loader />}>
                <Routes
                  routes={processedRoutes}
                  redirectTo={
                    roles === "affiliate_partner"
                      ? "/affiliateDemos"
                      : roles === "mentor"
                        ? "/mentorDashboard"
                        : "/demos"
                  }
                />
              </Suspense>
            </div>
          </div>
        )}
      </UnwatchedCountProvider>
    </ChatUnreadProvider>
  );
};
class App extends Component {
  componentDidMount() {}

  render() {
    // The SPR domain (REACT_APP_SPR_HOST) serves only the parents' report links; anything else
    // there is a 404 instead of Epicenter's login page or a dashboard.
    if (isSprHost()) {
      return (
        <Router>
          <FeatureFlagProvider>
            <SprHostApp />
          </FeatureFlagProvider>
        </Router>
      );
    }

    return (
      <Fragment>
        <StudentAlreadyPausedModal />
        <Router>
          <FeatureFlagProvider>
            <Suspense fallback={<Loader />}>
              <Switch>
                <Route path="/" component={Login} exact />
                <Route
                  path="/studentPortfolio/:studentUrlPath"
                  component={StudentPortfolio}
                  exact
                />
                <Route
                  path="/mentors/:mentorId"
                  component={MentorProfilePage}
                  exact
                />
                <Route path="/chatwithus" component={ChatWidget} exact />
                <Route
                  path="/bootCampLandingPage/:bootcampName"
                  component={BootCampLandingPage}
                  exact
                />
                <Route
                  path="/success-paymnet/:bootCampId"
                  component={SuccessPayment}
                  exact
                />
                {/* SPR v2 parent link — public, before the old /spr/... report route. */}
                <Route
                  path={SPR_PARENT_ROUTE}
                  exact
                  render={(props) => (
                    <Suspense fallback={<SprPageSkeleton />}>
                      <SprParentPage {...props} />
                    </Suspense>
                  )}
                />
                <Route
                  path={SPR_V1_REPORT_ROUTE}
                  component={StudentProfileReport}
                  exact
                />
                <Route
                  path="/assignAutoBatch/:courseId/:orderId"
                  component={SelectBatch}
                  exact
                />
                <Route
                  path={`/alchemy/alchemyFilePreview/:id`}
                  component={AlchemyFilePreview}
                />
  
                <Route
                  path={`/parentFeedback/:orderId/:scheduleId`}
                  component={ParentFeedback}
                  exact
                />
                <Route
                  path="/parentFeedback/:orderId/:scheduleId/1"
                  component={FirstClassFeedback}
                />
                <Route
                  path={`/successParentFeedback`}
                  component={SuccessFeedback}
                />
                <Route
                  path={`/approve-extra-class/:id`}
                  component={ApproveExtraClassReq}
                />
                <Route path="/teachers-day" component={TeacherDayPosts} exact />
                <Route path="/loop-action" component={LoopAction} exact />
                <Route path="/flags-debug" component={FeatureFlagsDebug} exact />
                <Route
                  path="/impersonate-app"
                  component={ImpersonateApp}
                  exact
                />
                {window.ReactNativeWebView && (
                  <Route path={"/native"} component={NativeComponent} exact />
                )}
                <Authentication>
                  <ProtectedRoute />
                </Authentication>
                <Redirect to="/" />
              </Switch>
            </Suspense>
          </FeatureFlagProvider>
        </Router>
      </Fragment>
    );
  }
}

export default App;
